import { Spacing } from '@persona-id/design-tokens';
import { useCallback, useContext } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import IconCamera from 'images/icons/inquiry/camera.svg';
import ImageIdBack from 'images/pictographs/id-back.svg';
import ImageIdBarcode from 'images/pictographs/id-barcode.svg';
import ImageIdFront from 'images/pictographs/id-front.svg';
import ImagePassportPortrait from 'images/pictographs/passport-portrait.svg';
import ImagePassportSignature from 'images/pictographs/passport-signature.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import Dialog from 'inquiry/components/Dialog';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import VerificationHero from 'inquiry/components/VerificationHero';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { tForGovernmentId } from 'lib/governmentId';
import { colorCss } from 'lib/inquiryStyles';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { PictographPosition } from 'lib/interfaces/ThemeSerialized';
import { ButtonComponentStyles, TextComponentStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { assertNever } from 'lib/utils/types';
import ButtonInputCapture from 'verify/components/ButtonInputCapture';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import { DisclaimerText } from 'verify/components/Disclaimer';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import {
  GovernmentIdCaptureOptionDesktop,
  GovernmentIdCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { MIN_HEIGHTS } from 'verify/styles/variables';

export interface InquiryGovernmentIdPromptViewProps {
  allowedCaptureOptions: Array<GovernmentIdCaptureOptionDesktop | GovernmentIdCaptureOptionMobile>;
  allowedFileTypes: StepGovernmentIdConfig['enabled-file-types'];
  enableVideoRecording?: boolean;
  assets: StepGovernmentIdConfig['assets']['prompt-page'];
  currentSide: GovernmentIdSide;
  governmentIdClass: GovernmentIdClass;
  handleDeviceHandoff: () => void;
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleInputCapture: (file: Blob) => void;
  handleSelectCamera: () => Promise<void>;
  hideCameraPermissionsDialog: () => void;
  isProcessing: PromptOptions;
  localizations: StepGovernmentIdConfig['localizations']['prompt-page'];
  openCameraPermissionsDialog: () => void;
  selectedCountryCode: StepGovernmentIdConfig['selected-country-code'];
  showCameraPermissionsDialog: boolean;
  /**
   * Indicates if the prompt view is being skipped due to webcam being the only
   * capture option. We use this flag because allowedCaptureOptions may still be
   * empty based on browser permissions.
   */
  skipPromptPage: boolean;
}

const InquiryGovernmentIdPromptView = ({
  allowedCaptureOptions,
  allowedFileTypes,
  enableVideoRecording,
  assets,
  currentSide,
  governmentIdClass,
  handleDeviceHandoff,
  handleDrop,
  handleInputCapture,
  handleSelectCamera: onSelectCamera,
  hideCameraPermissionsDialog,
  isProcessing,
  localizations,
  openCameraPermissionsDialog,
  selectedCountryCode,
  showCameraPermissionsDialog,
  skipPromptPage,
}: InquiryGovernmentIdPromptViewProps) => {
  const { governmentIdPictographPosition } = useContext(ThemeContext);
  const { base, buttons: buttonStyles } = useStepGovernmentIdStyleContext();

  const handleSelectCamera = useCallback(async () => {
    try {
      await onSelectCamera();
    } catch (e) {
      openCameraPermissionsDialog();
    }
  }, [onSelectCamera, openCameraPermissionsDialog]);

  // This only happens when the only capture option is web, skip prompt page is
  // configured, and user hasn't enabled camera permissions yet
  if (skipPromptPage && allowedCaptureOptions.length === 0) {
    return <FullScreenLoadingSpinner />;
  }

  // CenteredPictograph delegates a11y handling to Pictograph.
  const pictographNode = (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <CenteredPictograph height={null} width={null} role={null} aria-label={null}>
      <Pictograph assets={assets} currentSide={currentSide} governmentIdClass={governmentIdClass} />
    </CenteredPictograph>
  );

  const renderDisclaimer = selectedCountryCode === 'US' && currentSide === GovernmentIdSide.Back;

  return (
    <>
      <Dialog
        isOpen={showCameraPermissionsDialog}
        onClose={hideCameraPermissionsDialog}
        styles={base?.modal}
        data-test='camera-permissions-dialog'
        aria-labelledby='camera-permissions-dialog__title'
        aria-describedby='camer-permissions-dialog__description'
      >
        <ThemedTitle id='camera-permissions-dialog__title' styles={base?.titleStyle}>
          <Markdown>
            {enableVideoRecording
              ? localizations['camera-and-mic-permissions-title']
              : localizations['camera-permissions-title']}
          </Markdown>
        </ThemedTitle>
        <ThemedParagraph id='camera-permissions-dialog__description' styles={base?.textStyle}>
          <Markdown>
            {enableVideoRecording
              ? localizations['camera-and-mic-permissions-prompt']
              : localizations['camera-permissions-prompt']}
          </Markdown>
        </ThemedParagraph>
        <Spacer height={2} />
        <Button
          type='button'
          id='camera-permissions-dialog__btn'
          buttonType={ButtonType.Primary}
          styles={buttonStyles?.[ButtonType.Primary]}
          onClick={hideCameraPermissionsDialog}
        >
          {enableVideoRecording
            ? localizations['camera-and-mic-permissions-btn-continue-web']
            : localizations['camera-permissions-btn-continue-web']}
        </Button>
      </Dialog>

      <Container>
        <CustomAssetWithFallback
          customImage={assets['header-pictograph']}
          alt='header pictograph'
        />

        {governmentIdPictographPosition === PictographPosition.Start && pictographNode}

        <Header>
          <TitleWithCountrySpecificText
            currentSide={currentSide}
            governmentIdClass={governmentIdClass}
            override={localizations['title-override']}
            selectedCountryCode={selectedCountryCode}
            styles={base?.titleStyle}
          />
          <PromptWithCountrySpecificText
            currentSide={currentSide}
            governmentIdClass={governmentIdClass}
            override={
              allowedCaptureOptions.length === 0
                ? localizations['no-capture-options-prompt']
                : localizations['prompt-override']
            }
            styles={base?.textStyle}
          />
        </Header>

        {governmentIdPictographPosition === PictographPosition.Center && pictographNode}

        {renderDisclaimer && <DisclaimerText agreeToPolicyType='barcode' />}

        <PositionableButtonContainer>
          <CaptureOptions
            allowedCaptureOptions={allowedCaptureOptions}
            allowedFileTypes={allowedFileTypes}
            handleDeviceHandoff={handleDeviceHandoff}
            handleDrop={handleDrop}
            handleInputCapture={handleInputCapture}
            handleSelectCamera={handleSelectCamera}
            isProcessing={isProcessing}
            localizations={localizations}
          />
        </PositionableButtonContainer>
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[4]};
`;

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

const OptionButton = styled(Button)`
  margin-bottom: ${Spacing[4]};
  :last-of-type {
    margin-bottom: 0;
  }
`;

interface ImgCameraProps {
  styles: ButtonComponentStyles;
}

const ImgCamera = styled(IconCamera)<ImgCameraProps>`
  vertical-align: bottom;

  path[stroke] {
    stroke: ${(props) =>
      colorCss(props.styles?.['text-color']?.base?.base) ?? props.theme.colorFontButtonPrimary};
  }
`;

export default InquiryGovernmentIdPromptView;

interface CaptureOptionsProps
  extends Pick<
    InquiryGovernmentIdPromptViewProps,
    | 'allowedCaptureOptions'
    | 'allowedFileTypes'
    | 'handleDeviceHandoff'
    | 'handleDrop'
    | 'handleInputCapture'
    | 'handleSelectCamera'
    | 'isProcessing'
    | 'localizations'
  > {}

function CaptureOptions({
  allowedCaptureOptions,
  allowedFileTypes,
  handleDeviceHandoff,
  handleDrop,
  handleInputCapture,
  handleSelectCamera,
  isProcessing,
  localizations,
}: CaptureOptionsProps) {
  return (
    <CaptureOptionsContainer id='government-id-prompt__view'>
      {allowedCaptureOptions.map((option, index) => (
        <OptionBlock
          key={option}
          isPrimary={index === 0}
          option={option}
          handleDeviceHandoff={handleDeviceHandoff}
          handleDrop={handleDrop}
          handleInputCapture={handleInputCapture}
          handleSelectCamera={handleSelectCamera}
          isProcessing={isProcessing}
          localizations={localizations}
          allowedFileTypes={allowedFileTypes}
        />
      ))}
    </CaptureOptionsContainer>
  );
}

interface OptionBlockProps
  extends Pick<
    InquiryGovernmentIdPromptViewProps,
    | 'allowedFileTypes'
    | 'handleDeviceHandoff'
    | 'handleDrop'
    | 'handleInputCapture'
    | 'handleSelectCamera'
    | 'isProcessing'
    | 'localizations'
  > {
  isPrimary: boolean;
  option: GovernmentIdCaptureOptionDesktop | GovernmentIdCaptureOptionMobile;
}

function OptionBlock({
  allowedFileTypes,
  handleDeviceHandoff,
  handleDrop,
  handleInputCapture,
  handleSelectCamera,
  isPrimary,
  isProcessing,
  localizations,
  option,
}: OptionBlockProps) {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    accept: allowedFileTypes,
  });

  const { buttons: buttonStyles } = useStepGovernmentIdStyleContext();
  const buttonType = isPrimary ? ButtonType.Primary : ButtonType.Secondary;
  const [t] = useTranslation();

  const buttonDisabled = isProcessing != null;

  switch (option) {
    case 'web_camera':
      return (
        <OptionButton
          id='government-id-prompt__button--web-camera'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          onClick={handleSelectCamera}
          isPending={isProcessing === PromptOptions.SelectCamera}
          // note that localizations['btn-capture'] can be ''
          aria-label={
            localizations['btn-capture'] || t('government-id.prompt.btn-take-photo-label')
          }
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <CaptureButtonContent styles={buttonStyles?.[buttonType]}>
            {localizations['btn-capture']}
          </CaptureButtonContent>
        </OptionButton>
      );

    case 'mobile_camera':
      return (
        <ButtonInputCapture
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          id='government-id-prompt__button--mobile-camera'
          handleSelectImage={handleInputCapture}
          isPending={isProcessing === PromptOptions.UploadImage}
          // note that localizations['btn-capture'] can be ''
          aria-label={
            localizations['btn-capture-mobile-camera'] ||
            localizations['btn-capture'] ||
            t('government-id.prompt.btn-take-photo-label')
          }
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <CaptureButtonContent styles={buttonStyles?.[buttonType]}>
            {localizations['btn-capture-mobile-camera'] || localizations['btn-capture']}
          </CaptureButtonContent>
        </ButtonInputCapture>
      );

    case 'upload':
      return (
        <OptionButton
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          {...getRootProps()}
        >
          <input {...getInputProps()} data-test='file-upload' data-cy='file-upload' />
          {localizations['btn-upload']}
        </OptionButton>
      );

    case 'device_handoff':
      return (
        <OptionButton
          id='government-id-prompt__button--device-handoff'
          data-test='government-id-prompt__button--device-handoff'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          onClick={handleDeviceHandoff}
          isPending={isProcessing === PromptOptions.DeviceHandoff}
        >
          {localizations['btn-device-handoff']}
        </OptionButton>
      );

    default:
      assertNever(option);
  }
}

interface CaptureButtonContentProps {
  children: string | undefined;
  styles: ButtonComponentStyles;
}

const CaptureButtonContent = ({ children, styles }: CaptureButtonContentProps) => {
  if (children) {
    return <span>{children}</span>;
  } else {
    return <ImgCamera data-test='government-id-prompt__image--camera' styles={styles} />;
  }
};

const StyledCustomizablePictograph = styled(CustomizablePictograph)`
  width: 100%;
`;

const Pictograph = ({
  assets,
  currentSide,
  governmentIdClass,
}: {
  assets: StepGovernmentIdConfig['assets']['prompt-page'];
  currentSide: GovernmentIdSide;
  governmentIdClass: GovernmentIdClass;
}) => {
  const { base } = useStepGovernmentIdStyleContext();

  if (governmentIdClass === 'pp' || governmentIdClass === 'ipp') {
    switch (currentSide) {
      case GovernmentIdSide.PassportSignature:
        return (
          <CustomAssetWithFallback
            customImage={assets['passport-signature-pictograph']}
            alt='passport signature pictograph'
          >
            <StyledCustomizablePictograph
              url='passportSignaturePictographUrl'
              height='passportSignaturePictographHeight'
              aria-label='passport signature'
              vectorEffect='initial'
              primaryColor={colorCss(base?.fill.color)}
              highlightColor={colorCss(base?.fill.highlightColor)}
              backgroundColor={colorCss(base?.fill.backgroundColor)}
              strokeColor={colorCss(base?.stroke.color)}
            >
              <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
                <ImagePassportSignature height={64} width='100%' />
              </VerificationHero>
            </StyledCustomizablePictograph>
          </CustomAssetWithFallback>
        );
      default:
        return (
          <CustomAssetWithFallback
            customImage={assets['passport-front-pictograph']}
            alt='passport front pictograph'
          >
            <StyledCustomizablePictograph
              url='passportFrontPictographUrl'
              height='passportFrontPictographHeight'
              aria-label='passport front'
              vectorEffect='initial'
              primaryColor={colorCss(base?.fill.color)}
              highlightColor={colorCss(base?.fill.highlightColor)}
              backgroundColor={colorCss(base?.fill.backgroundColor)}
              strokeColor={colorCss(base?.stroke.color)}
            >
              <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
                <ImagePassportPortrait height={80} width='100%' />
              </VerificationHero>
            </StyledCustomizablePictograph>
          </CustomAssetWithFallback>
        );
    }
  }

  switch (currentSide) {
    case GovernmentIdSide.Back:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-back-pictograph']}
          alt='ID back pictograph'
        >
          <StyledCustomizablePictograph
            url='idBackPictographUrl'
            height='idBackPictographHeight'
            aria-label='id back'
            vectorEffect='initial'
            primaryColor={colorCss(base?.fill.color)}
            highlightColor={colorCss(base?.fill.highlightColor)}
            backgroundColor={colorCss(base?.fill.backgroundColor)}
            strokeColor={colorCss(base?.stroke.color)}
          >
            <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
              <ImageIdBack height={64} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.BarcodePdf417:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-back-pictograph']}
          alt='ID back pictograph'
        >
          <StyledCustomizablePictograph
            url='idBackPictographUrl'
            height='idBackPictographHeight'
            aria-label='id back'
            vectorEffect='initial'
            primaryColor={colorCss(base?.fill.color)}
            highlightColor={colorCss(base?.fill.highlightColor)}
            backgroundColor={colorCss(base?.fill.backgroundColor)}
            strokeColor={colorCss(base?.stroke.color)}
          >
            <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
              <ImageIdBarcode height={64} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.Front:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-front-pictograph']}
          alt='ID front pictograph'
        >
          <StyledCustomizablePictograph
            url='idFrontPictographUrl'
            height='idFrontPictographHeight'
            aria-label='id front'
            vectorEffect='initial'
            primaryColor={colorCss(base?.fill.color)}
            highlightColor={colorCss(base?.fill.highlightColor)}
            backgroundColor={colorCss(base?.fill.backgroundColor)}
            strokeColor={colorCss(base?.stroke.color)}
          >
            <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
              <ImageIdFront height={64} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.FrontOrBack:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-front-pictograph']}
          alt='ID front pictograph'
        >
          <StyledCustomizablePictograph
            url='idFrontPictographUrl'
            height='idFrontPictographHeight'
            aria-label='id front'
            vectorEffect='initial'
            primaryColor={colorCss(base?.fill.color)}
            highlightColor={colorCss(base?.fill.highlightColor)}
            backgroundColor={colorCss(base?.fill.backgroundColor)}
            strokeColor={colorCss(base?.stroke.color)}
          >
            <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
              <ImageIdFront height={48} width='100%' />
              <ImageIdBack height={48} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.PassportSignature:
      return (
        <CustomAssetWithFallback
          customImage={assets['passport-signature-pictograph']}
          alt='passport signature pictograph'
        >
          <StyledCustomizablePictograph
            url='passportSignaturePictographUrl'
            height='passportSignaturePictographHeight'
            aria-label='passport signature'
            vectorEffect='initial'
            primaryColor={colorCss(base?.fill.color)}
            highlightColor={colorCss(base?.fill.highlightColor)}
            backgroundColor={colorCss(base?.fill.backgroundColor)}
            strokeColor={colorCss(base?.stroke.color)}
          >
            <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
              <ImagePassportSignature height={64} width='100%' />
            </VerificationHero>
          </StyledCustomizablePictograph>
        </CustomAssetWithFallback>
      );

    default:
      assertNever(currentSide);
  }
};

const TitleWithCountrySpecificText = ({
  selectedCountryCode,
  governmentIdClass,
  currentSide,

  /**
   * Override for client-side copy that is based on ID type + country.
   */
  override = '',
  styles,
}: {
  selectedCountryCode: StepGovernmentIdConfig['selected-country-code'];
  governmentIdClass: GovernmentIdClass;
  currentSide: GovernmentIdSide;
  override: string | undefined;
  styles?: TextComponentStyles;
}) => {
  const [t] = useTranslation();

  return (
    <ThemedTitle data-test='government-id-prompt__title' styles={styles}>
      <Markdown>
        {override.length > 0
          ? override
          : // TODO: allow custom copy for Dynamic Flow
            tForGovernmentId(
              t,
              ['government-id.prompt.title', 'government-id.title'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              selectedCountryCode,
              governmentIdClass,
              currentSide,
            )}
      </Markdown>
    </ThemedTitle>
  );
};

const PromptWithCountrySpecificText = ({
  governmentIdClass,
  currentSide,
  /**
   * Override for client-side copy that is based on ID type + country.
   */
  override = '',
  styles,
}: {
  governmentIdClass: GovernmentIdClass;
  currentSide: GovernmentIdSide;
  override: string | undefined;
  styles?: TextComponentStyles;
}) => {
  const [t] = useTranslation();

  return (
    <ThemedParagraph data-test='government-id-prompt__prompt' styles={styles}>
      <Markdown>
        {override.length > 0
          ? override
          : tForGovernmentId(
              t,
              ['government-id.prompt.prompt'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              null,
              governmentIdClass,
              currentSide,
            )}
      </Markdown>
    </ThemedParagraph>
  );
};
